import {useCallback, useReducer, useMemo, useRef} from "react";
import React from "react";
import _ from "lodash";

export const extractValue = (row, accessor) => {
    return _.get(row, accessor);
}

function addIdToTreeRows(rows, childKey, parentId) {
    _.forEach(rows, function(row, index) {
        if (_.isUndefined(row.id)) {
            row.id = _.isUndefined(parentId) ? index : `${parentId}-${index}`;
        }
        if(!_.isEmpty(row[childKey])) {
            addIdToTreeRows(row[childKey], childKey, row.id);
        }         
    });
    return rows;
}

function getFlattenTreeNodes(rows, childKey) {
    const flattenTreeNodes = [];
    _.forEach(rows, function(row) {
        if(!_.isEmpty(row[childKey])) {
            flattenTreeNodes.push(...getFlattenTreeNodes(row[childKey], childKey));
        } 
        flattenTreeNodes.push(row);
    });
    return flattenTreeNodes;
}

function sortTreeNodes(sortState, columnIdToSort, rows, childKey) {
    const sortedNodes = _.orderBy(rows, [columnIdToSort], [sortState.sortOrder]);
    _.forEach(sortedNodes, function (row) {
        if (!_.isEmpty(row[childKey])) {
            row[childKey] = sortTreeNodes(
                sortState,
                columnIdToSort,
                row[childKey],
                childKey,
            );
        }
    });
    return sortedNodes;
};


const sort = (sortState, columnsMap, rows) => {
    return _.orderBy(rows, [columnsMap[sortState.column].accessor], [sortState.sortOrder]);
};

const defaultFilter = (value, row, filter) => {
    if(_.isArray(value)){
        const existsOne = _.find(value, subValue => {
            return filter.values[subValue]
        });
        return !!existsOne;
    } else {
        return !!filter.values[value];
    }
}

const filter = (filterState, rows, columns, columnsMap) => {
    const {textFilter} = filterState;
    return _.filter(rows, (row) => {
        const lowerCaseTextFilter = textFilter && textFilter.toLowerCase();
        let isTextFilterMatch = !textFilter;


        if(textFilter) {
            _.forEach(columns, (column) => {
                const value = extractValue(row, column.accessor);
                if (_.isNumber(value) && _.toString(value).includes(lowerCaseTextFilter)) {
                    isTextFilterMatch = true;
                    return false;
                } else if(_.isString(value) && value.toLowerCase().includes(lowerCaseTextFilter)) {
                    isTextFilterMatch = true;
                    return false;
                } else if (_.isArray(value) && value.join(", ").toLowerCase().includes(lowerCaseTextFilter)) {
                    isTextFilterMatch = true;
                    return false;
                }
            });
        }

        let isMultiFilterMatch = true;

        _.forEach(filterState.columns, (filter, key) => {
            if(_.isObject(filter) && filter.activeCount > 0) {
                const { accessor, filter: filterDefinition } = columnsMap[key];
                const value = extractValue(row, accessor);
                const filterFunc = filterDefinition?.filter || defaultFilter;
                const match = filterFunc(value, row, filter);
                if (!match) {
                    isMultiFilterMatch = false;
                    return false;
                }
            }
        });

        return isTextFilterMatch && isMultiFilterMatch;
    });
};

const init = (initialState) => {
    let sortState = null;

    if(initialState.isSorting) {
        sortState = {
            column: _.isString(initialState.columns[0]) ? initialState.columns[0] : (initialState.columns[0].id || "0"),
            sortOrder: "asc"
        }
    }

    const initialPageState = initialState.pageSize? {pageIndex: 0} : undefined;

    let selectionState = undefined;
    const { isMultipleSelection, isSelectCurrentPageEnabled, isSelectAllPagesEnabled, isRowSelecting } = initialState.selectionConfig;

    if (initialState.defaultSelectionState) {
        let count = _.values(initialState.defaultSelectionState.rows).filter(value => !!value).length;
        let rows = initialState.defaultSelectionState.rows;

        selectionState = {
            rows,
            count,
            isDirty: false,
            isSelectAllPagesEnabled,
            isSelectCurrentPageEnabled,
            isMulti: isMultipleSelection !== false,
        };
    } else if (isRowSelecting) {
        selectionState = {
            rows: {},
            count: 0,
            isDirty: false,
            isSelectAllPagesEnabled,
            isSelectCurrentPageEnabled,
            isMulti: isMultipleSelection !== false,
        };
    }

    // Adds Column State for each column, if not provided.
    const defaultCoulumns = () => {
        const cols = {};
        _.forEach(initialState.columns, (column) => {
            if(column.mustShow === true) {
                cols[column.id] = { mustShow: true, isHidden: false };
            }
            else if (initialState.defaultColumnState && initialState.defaultColumnState[column.id]) {
                cols[column.id] = { mustShow: false, isHidden: true };
            }
            else {
                cols[column.id] = { mustShow: false, isHidden: false };
            }
        });
        return cols;
    }

    return {
        sortState: initialState.defaultSortState || sortState,
        defaultSortState: initialState.defaultSortState || sortState,
        defaultFilterState: initialState.defaultFilterState || {columns: {}, count: 0},
        tableState: {...initialState.defaultTableState} || {},
        rowState: {},
        columnState: {...defaultCoulumns(),...(initialState.savedColumnState || initialState.defaultColumnState)} , 
        selectionState,
        filterState: initialState.defaultFilterState || {columns: {}, count: 0},
        pageState: initialPageState,
        initialPageState
    };
};

const reducer = (state, action) => {
    const {type, payload} = action;

    switch (type) {
        case "TOGGLE-SORT": {
            const { id } = payload;

            let newSortState = {};
            const currentSortOrder = state.sortState.sortOrder;
            const currentSortColumn = state.sortState.column;
            if(currentSortColumn === id){
                if(currentSortOrder === "asc") {
                    newSortState.sortOrder = "desc";
                    newSortState.column = id;
                } else if(currentSortColumn !== state.defaultSortState.column) {
                    newSortState = state.defaultSortState;
                } else {
                    newSortState.sortOrder = "asc";
                    newSortState.column = id;
                }
            } else {
                newSortState.sortOrder = "asc";
                newSortState.column = id;
            }

            return {...state, sortState: newSortState, pageState: state.initialPageState}
        }
        case "SET-FILTER": {
            const { id, values, filteredRowCount } = payload;

            const newValues = _.pickBy(values);

            const newColumnFilter = {
                values: newValues,
                valuesArray: _.values(values),
                activeCount: _.size(newValues)
            };

            const columns = {...state.filterState.columns, [id]: newColumnFilter};
            let count = 0;

            _.forEach(columns, (state) => {
                if(state.activeCount > 0) {
                    count = count + 1;
                }
            });

            return { ...state, selectionState: { ...state.selectionState, filteredRowCount }, filterState: { ...state.filterState, columns, count }, pageState: state.initialPageState }
        }
        case "RESET-FILTERS": {
            const { filteredRowCount } = payload;
            return { ...state, selectionState: { ...state.selectionState, filteredRowCount }, filterState: { ...state.filterState, ...state.defaultFilterState }, pageState: state.initialPageState }
        }
        case "UPDATE-ROW": {
            const {id, state: newState} = payload;
            const currentState = state.rowState[id] || {};

            return { ...state, rowState: {...state.rowState, [id]: {...currentState, ...newState}}}
        }
        case "UPDATE-COLUMN-STATE": {
            return { ...state, columnState: payload.state}
        }
        case "TOGGLE-ROW-SELECTION": {
            const { id, value, filteredRowCount } = payload;

            if(state.selectionState.isMulti) {
                let count = state.selectionState.count;
                const currentValue = state.selectionState.rows[id] || false;
                if(currentValue && !value) {
                    count = count -1;
                } else if (!currentValue && value) {
                    count = count + 1;
                }

                return { ...state, selectionState: { ...state.selectionState, count, rows: { ...state.selectionState.rows, [id]: value }, isMulti: true, isDirty: true, filteredRowCount } };
            } else {
                if(!value) {
                    return state;
                } else {
                    return { ...state, selectionState: { ...state.selectionState, count: 1, rows: { [id]: true }, isMulti: false, isDirty: true, filteredRowCount } }
                }
            }
        }
        case "TOGGLE-SELECT-ALL": {
            const { value, rows, resetDirty, filteredRowCount } = payload;

            if (!state.selectionState.isMulti) {
                return state;
            }

            const selectionState = {
                ...state.selectionState,
                rows: {},
                count: 0,
                filteredRowCount,
                isDirty: !resetDirty
            };

            if (value) {
                _.forEach(rows, (row) => {
                    selectionState.rows[row.id] = value;
                });
                selectionState.count = rows.length
            }
            selectionState.allSelected = selectionState.count === filteredRowCount;

            return { ...state, selectionState };
        }
        case "UPDATE-TEXT-FILTER": {
            const { value, filteredRowCount } = payload;
            const updatedFilterState = { ...state.filterState, textFilter: value };

            if (_.isEqual(updatedFilterState, state.filterState)) {
                return { ...state, selectionState: { ...state.selectionState, filteredRowCount }, pageState: state.initialPageState };
            }
            return { ...state, selectionState: { ...state.selectionState, filteredRowCount }, filterState: updatedFilterState, pageState: state.initialPageState };
        }
        case "GO-TO-PAGE": {
            return {...state, pageState: {pageIndex: payload.index}}
        }
    }

    return state;
};

export default ({ isSorting, isRowSelecting, isMultiSelect, columns, rows: data, externalSort, externalFilter, defaultSortState, defaultFilterState, defaultSelectionState, pageSize, defaultColumnState, savedColumnState, renderMode = { type: 'list' }, selectionConfig }) => {
    const _selectionConfig = {
        isRowSelecting,
        isMultipleSelection: isMultiSelect,
        ...selectionConfig
    }

    const processedRows = useMemo(() => {
        if(renderMode.type === 'tree') {
            const deepCopyRows = _.cloneDeep(data);
            return addIdToTreeRows(deepCopyRows, renderMode.childKey);
        } else {
            return _.map(data, (row, index) => ({...row, id: _.isUndefined(row.id) ? index : row.id}))
        }
    }, [data]);

    const {pColumns, columnsMap} = useMemo(() => {
        const columnsMap = {};

        const pColumns = columns.map((column, index) => {
            let pColumn = null;
            if(_.isString(column)) {
                pColumn = {id: column, accessor: column}
            } else {
                pColumn = {...column, id: _.isUndefined(column.id) ? index.toString() : column.id};
            }
            columnsMap[pColumn.id] = pColumn;
            return pColumn;
        });

        return {pColumns, columnsMap}
    }, [columns]);

    const [table, dispatch] = useReducer(reducer, { columns, isSorting, selectionConfig: _selectionConfig, defaultSortState, defaultFilterState, defaultSelectionState, pageSize, defaultColumnState, savedColumnState }, init);
    const tableRef = useRef(null);
    tableRef.current = table;

    const shownColumns = useMemo(() => {
        return pColumns.filter(column => {
            return !table.columnState[column.id]?.isHidden;
        });
    }, [pColumns, table.columnState]);

    const toggleSort = useCallback(({id}) => {
        dispatch({
            type: "TOGGLE-SORT",
            payload: {
                id
            }
        })
    }, [dispatch]);

    const updateFilterState = useCallback((payload) => {
        dispatch({
            type: "SET-FILTER",
            payload: {
                ...payload,
                filteredRowCount: renderMode.type === 'tree' ? getFlattenTreeNodes(organizedRows, renderMode.childKey).length : organizedRows.length
            },
        })
    }, [dispatch]);

    const resetFilters = useCallback(() => {
        dispatch({
            type: "RESET-FILTERS",
            payload: {
                filteredRowCount: renderMode.type === 'tree' ? getFlattenTreeNodes(organizedRows, renderMode.childKey).length : organizedRows.length
            }
        })
    }, [dispatch]);

    const updateRowState = useCallback( id => stateOrFunc => {
        let state = _.isFunction(stateOrFunc) ? stateOrFunc(tableRef.current.rowState[id]) : stateOrFunc;

        dispatch({
            type: "UPDATE-ROW",
            payload: {
                id,
                state
            }
        })
    }, [dispatch]);

    const updateColumnState = useCallback( state => {
        dispatch({
            type: "UPDATE-COLUMN-STATE",
            payload: {
                state
            }
        })
    }, [dispatch]);

    const restoreColumnStateDefaults = useCallback( () => {
        updateColumnState(defaultColumnState || {});
    }, [updateColumnState]);

    const updateTextFilter = useCallback((value) => {
        dispatch({
            type: "UPDATE-TEXT-FILTER",
            payload: {
                value,
                filteredRowCount: renderMode.type === 'tree' ? getFlattenTreeNodes(organizedRows, renderMode.childKey).length : organizedRows.length
            }
        })
    }, [dispatch]);

    const filteredRows = useMemo(() => {
        if((table.filterState.count > 0 || table.filterState.textFilter) && !externalFilter) {
            return filter(table.filterState, processedRows, pColumns, columnsMap)
        } else {
            return processedRows;
        }
    }, [table.filterState, processedRows, externalFilter, pColumns, columnsMap]);

    const organizedRows = useMemo(() => {
        if(isSorting && !externalSort) { 
            if(renderMode.type === 'tree') {
                return sortTreeNodes(table.sortState, columnsMap[table.sortState.column].accessor, filteredRows, renderMode.childKey);
            } else {
                return sort(table.sortState, columnsMap, filteredRows);
            }
        } else {
            return filteredRows;
        }
    }, [isSorting, externalSort, filteredRows, table.sortState, columnsMap]);

    const toggleRowSelection = useCallback(id => value => {
        dispatch({
            type: "TOGGLE-ROW-SELECTION",
            payload: {
                id,
                value,
                filteredRowCount: renderMode.type === 'tree' ? getFlattenTreeNodes(organizedRows, renderMode.childKey).length : organizedRows.length
            }
        })
    }, [dispatch]);

    const pageChunks = useMemo(() => {
        if (pageSize) {
            return _.chunk(organizedRows, pageSize);
        } else {
            return null;
        }
    }, [organizedRows, pageSize]);

    const selectAllRows = useCallback((value, resetDirty = false, isPageLevelSelectionEnabled = false) => {
        let pageRows = isPageLevelSelectionEnabled && pageChunks && table.pageState ? (pageChunks[table.pageState.pageIndex] ?? pageChunks[0] ?? []) : organizedRows;

        dispatch({
            type: "TOGGLE-SELECT-ALL",
            payload: {
                value,
                rows: renderMode.type === 'tree' ? getFlattenTreeNodes(pageRows, renderMode.childKey) : pageRows,
                resetDirty,
                filteredRowCount: renderMode.type === 'tree' ? getFlattenTreeNodes(organizedRows, renderMode.childKey).length : organizedRows.length
            }
        })
    }, [dispatch, organizedRows, pageChunks, table.pageState]);

    const gotoPage = (i) => {
        if(i >= 0 ) {
            dispatch({
                type: "GO-TO-PAGE",
                payload: {
                    index: i
                }
            })
        }
    };

    const pagination = useMemo(() => {
        if(pageChunks) {
            const pageIndex = table.pageState.pageIndex;
            const pageCount = pageChunks.length;
           
            return {
                pageIndex,
                pageRows: pageChunks[pageIndex],
                pageCount,
                gotoPage
            }
        } else {
            return null;
        }
    }, [pageChunks, table.pageState, dispatch]);

    return useMemo(() => ({
        rows: processedRows,
        organizedRows,
        columns: shownColumns,
        sortState: table.sortState,
        filterState: table.filterState,
        tableState: table.tableState,
        rowState: table.rowState,
        selectionState: table.selectionState,
        columnState: table.columnState,
        toggleSort,
        updateFilterState,
        updateRowState,
        updateTextFilter,
        toggleRowSelection,
        selectAllRows,
        resetFilters,
        pagination,
        updateColumnState,
        restoreColumnStateDefaults,
        gotoPage
    }), [table, organizedRows, toggleSort, updateFilterState, updateRowState, updateTextFilter, toggleRowSelection, selectAllRows, processedRows, resetFilters, pagination, updateColumnState, restoreColumnStateDefaults])
};
